import { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Briefcase, Clock, CalendarRange, Hourglass, Lock as LockIcon, AlertTriangle } from "lucide-react";

const MANAGER_STATUSES = ["Draft", "Assigned", "Ready", "In Progress", "Submitted", "Approved", "Accepted", "Rejected", "Paid"];
const SIMPLE_STATUSES = ["Not-started", "In-progress", "Completed"];

/** Maps backend lifecycle status -> tester-facing 3-state status. */
export function toSimpleStatus(status) {
  if (status === "Assigned" || status === "Draft") return "Not-started";
  if (status === "Ready") return "Ready";
  if (status === "In Progress" || status === "Rejected") return "In-progress";
  if (status === "Submitted" || status === "Accepted" || status === "Approved" || status === "Paid") return "Completed";
  return "Not-started";
}

/** Map backend status to the admin-facing label per spec. */
export function toAdminLabel(status) {
  const map = {
    "Draft": "Draft",
    "Assigned": "Pending acceptance",
    "Ready": "Ready to test",
    "In Progress": "In progress",
    "Submitted": "Completed",
    "Approved": "Approved",
    "Accepted": "Approved",
    "Rejected": "Rejected",
    "Paid": "Paid",
  };
  return map[status] || status;
}

const SIMPLE_BADGE_STYLES = {
  "Not-started":  "bg-slate-100 text-slate-700 border-slate-300",
  "Ready":        "bg-cyan-50 text-cyan-800 border-cyan-300",
  "In-progress":  "bg-amber-50 text-amber-800 border-amber-300",
  "Completed":    "bg-emerald-50 text-emerald-700 border-emerald-300",
};

export function SimpleStatusBadge({ value, testid }) {
  const cls = SIMPLE_BADGE_STYLES[value] || SIMPLE_BADGE_STYLES["Not-started"];
  return <span className={`pill border ${cls} font-medium`} data-testid={testid}>{value}</span>;
}

/** Format ISO date as "Mon DD, hh:mm AM/PM". */
function fmtBoundary(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date}, ${time}`;
}

/** Returns hours remaining until ISO end date. Negative if past due. */
function hoursLeft(endIso) {
  if (!endIso) return null;
  const ms = new Date(endIso).getTime() - Date.now();
  return ms / 3600000;
}

export function DueHoursCountdown({ endIso, completed }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (completed) return;
    const t = setInterval(() => force(x => x + 1), 60000);
    return () => clearInterval(t);
  }, [completed]);

  if (!endIso) return <span className="text-slate-400 text-sm">—</span>;
  if (completed) return <span className="text-emerald-700 text-sm font-medium" data-testid="due-hours-completed">Locked</span>;
  const h = hoursLeft(endIso);
  if (h == null) return <span className="text-slate-400 text-sm">—</span>;
  if (h < 0) return <span className="text-red-700 text-sm font-semibold flex items-center gap-1" data-testid="due-hours"><AlertTriangle className="w-3.5 h-3.5"/> Overdue</span>;
  const display = h < 1 ? `${Math.round(h * 60)} min Left` : `${Math.round(h)} Hours Left`;
  const color = h < 6 ? "text-red-700" : h < 24 ? "text-amber-700" : "text-slate-700";
  return <span className={`text-sm font-semibold ${color}`} data-testid="due-hours">{display}</span>;
}

export default function Tasks() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [q, setQ] = useState("");
  const isFreelancer = user?.role === "freelancer";
  const [status, setStatus] = useState(sp.get("status") || "");

  useEffect(() => {
    (async () => {
      const params = {};
      if (!isFreelancer && status) params.status = status;
      const { data } = await api.get("/tasks", { params });
      setTasks(data || []);
    })();
  }, [status, isFreelancer]);

  const filteredFreelancer = useMemo(() => {
    const lc = q.toLowerCase();
    return tasks.filter(t => {
      if (q && !(t.title + " " + t.project_name).toLowerCase().includes(lc)) return false;
      if (status && toSimpleStatus(t.status) !== status) return false;
      return true;
    });
  }, [tasks, q, status]);

  const filteredOther = useMemo(() => {
    const lc = q.toLowerCase();
    return tasks.filter(t => !q || (t.title + " " + t.project_name).toLowerCase().includes(lc));
  }, [tasks, q]);

  if (isFreelancer) {
    return (
      <AppLayout title="My Tasks" subtitle="Active assignments">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="relative max-w-sm w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <Input className="pl-9" placeholder="Search tasks or projects" value={q} onChange={e=>setQ(e.target.value)} data-testid="input-search-tasks"/>
          </div>
          <Select
            value={status || "all"}
            onValueChange={v => { const nv = v === "all" ? "" : v; setStatus(nv); if (nv) sp.set("status", nv); else sp.delete("status"); setSp(sp); }}
          >
            <SelectTrigger className="w-56" data-testid="select-status-filter"><SelectValue placeholder="All statuses"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {SIMPLE_STATUSES.map(s => <SelectItem key={s} value={s} data-testid={`filter-opt-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tester-task-grid">
          {filteredFreelancer.map(t => <TesterTaskCard key={t.id} t={t} onOpen={()=>nav(`/tasks/${t.id}`)} />)}
          {filteredFreelancer.length === 0 && (
            <div className="col-span-full bg-white border border-slate-200 rounded-md py-10 text-center text-slate-500" data-testid="empty-tester-tasks">No tasks match the current filter.</div>
          )}
        </div>
      </AppLayout>
    );
  }

  // Admin / Manager / Client legacy view
  return (
    <AppLayout title="Tasks" subtitle="Work items">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <Input className="pl-9" placeholder="Search tasks" value={q} onChange={e=>setQ(e.target.value)} data-testid="input-search-tasks"/>
        </div>
        <Select value={status || "all"} onValueChange={v => { const nv = v === "all" ? "" : v; setStatus(nv); if (nv) sp.set("status", nv); else sp.delete("status"); setSp(sp); }}>
          <SelectTrigger className="w-56" data-testid="select-status-filter"><SelectValue placeholder="All statuses"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {MANAGER_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="hidden md:block bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table">
          <thead><tr>
            <th>Task</th><th>Project</th><th>Status</th><th>Assignee</th><th>Payout</th><th>Due</th>
          </tr></thead>
          <tbody>
            {filteredOther.map(t => (
              <tr key={t.id} className="cursor-pointer" onClick={()=>nav(`/tasks/${t.id}`)} data-testid={`row-task-${t.id}`}>
                <td><span className="font-mono text-[11px] text-slate-400 mr-2">{t.id.slice(0,6).toUpperCase()}</span>{t.title}</td>
                <td>{t.project_name}</td>
                <td><StatusBadge kind="task" value={t.status}/></td>
                <td>{t.assignee_name || "—"}</td>
                <td className="font-mono">${(t.payout_amount||0).toFixed(2)}</td>
                <td>{t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
            {filteredOther.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No tasks.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="tasks-mobile-list">
        {filteredOther.length === 0 && <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-slate-500">No tasks.</div>}
        {filteredOther.map(t => (
          <button key={t.id} onClick={()=>nav(`/tasks/${t.id}`)} className="w-full text-left bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors" data-testid={`card-task-${t.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[10px] text-slate-400 mb-0.5">{t.id.slice(0,6).toUpperCase()}</div>
                <div className="font-display font-semibold text-slate-900 text-sm line-clamp-2">{t.title}</div>
              </div>
              <StatusBadge kind="task" value={t.status}/>
            </div>
            <div className="text-xs text-slate-500 mb-2">{t.project_name}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
              <span>{t.assignee_name || "Unassigned"}</span>
              <span className="text-slate-400">•</span>
              <span className="font-mono font-medium text-slate-900">${(t.payout_amount||0).toFixed(2)}</span>
              {t.due_date && (
                <>
                  <span className="text-slate-400">•</span>
                  <span>Due {new Date(t.due_date).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </button>
        ))}
      </div>
    </AppLayout>
  );
}

function TesterTaskCard({ t, onOpen }) {
  const simple = toSimpleStatus(t.status);
  const completed = simple === "Completed";
  const cardRef = useRef(null);
  return (
    <div
      ref={cardRef}
      className={`kpi-card cursor-pointer transition hover:shadow-md hover:border-slate-300 ${completed ? "opacity-90" : ""}`}
      onClick={onOpen}
      data-testid={`task-card-${t.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase text-slate-500 tracking-wider flex items-center gap-1.5" data-testid={`task-project-${t.id}`}>
            <Briefcase className="w-3 h-3"/> {t.project_name || "—"}
          </div>
          <h3 className="font-display font-semibold text-slate-900 mt-1 line-clamp-2" data-testid={`task-title-${t.id}`}>{t.title}</h3>
        </div>
        <SimpleStatusBadge value={simple} testid={`task-status-${t.id}`}/>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
        <Field icon={Briefcase} label="Task Type" testid={`task-type-${t.id}`}>
          <span className="font-medium text-slate-900">{t.task_type || "Functional"}</span>
        </Field>
        <Field icon={Clock} label="Total Allocated" testid={`task-allocated-${t.id}`}>
          <span className="font-medium text-slate-900">{(t.allocated_hours ?? 0)} Hours Total</span>
        </Field>
        <Field icon={Hourglass} label="Due Hours" testid={`task-duehours-${t.id}`}>
          <DueHoursCountdown endIso={t.due_date} completed={completed}/>
        </Field>
        <Field icon={CalendarRange} label="Due Timeline" testid={`task-timeline-${t.id}`}>
          <span className="text-slate-900 text-xs font-medium leading-snug">
            {fmtBoundary(t.start_at || t.created_at)} — {fmtBoundary(t.due_date)}
          </span>
        </Field>
      </div>

      {completed && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5" data-testid={`task-locked-${t.id}`}>
          <LockIcon className="w-3 h-3"/> Locked – no further edits
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase text-slate-500 tracking-wider flex items-center gap-1"><Icon className="w-3 h-3"/>{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
